import axios from "axios";
import { v4 as uuidv4 } from "uuid";

import { DataType, FeatureType, HttpMethod } from "@/jenesys-types/data/enums";

import environmentConfig from "@/util/environments";
import { performanceMonitor } from "@/utils/performance";

export const authedFetch = axios.create({
  baseURL: environmentConfig.apiEndpoint
});

// Add the X-Request-ID header to all requests
authedFetch.interceptors.request.use((config) => {
  // eslint-disable-next-line no-param-reassign
  config.headers = config.headers || {}; // Ensure headers object exists
  // eslint-disable-next-line no-param-reassign
  config.headers["X-Request-ID"] = uuidv4();
  return config;
});

// Constants for URL extraction regex patterns
const URL_COMPANY_ID_REGEX = /\/company\/([^/]+)/;
const URL_CLIENT_ID_REGEX = /\/client\/([^/]+)/;

/**
 * Utility function to ensure companyId is present in pathParams
 * This extracts company or client ID from URL if not provided in pathParams
 * @param pathParams - The path parameters object to enrich
 * @returns The enriched path parameters with companyId
 */
export const ensureCompanyId = (pathParams: PathParams = {}): PathParams => {
  // If companyId is already provided, use it
  if (pathParams.companyId) {
    return pathParams;
  }

  // If clientId is provided, use it as companyId
  if (pathParams.clientId) {
    return {
      ...pathParams,
      companyId: pathParams.clientId
    };
  }

  // Try to extract from URL
  const path = window.location.pathname;
  const clientMatch = URL_CLIENT_ID_REGEX.exec(path);
  const companyMatch = URL_COMPANY_ID_REGEX.exec(path);

  // Try to get client ID first (preferred)
  if (clientMatch) {
    const [, clientId] = clientMatch;
    return {
      ...pathParams,
      companyId: clientId
    };
  }

  // Fall back to company ID
  if (companyMatch) {
    const [, companyId] = companyMatch;
    return {
      ...pathParams,
      companyId
    };
  }

  // Check localStorage as last resort
  const storedCompanyId = localStorage.getItem("activeCompanyId");
  const storedClientId = localStorage.getItem("activeClientId");

  if (storedClientId) {
    return {
      ...pathParams,
      companyId: storedClientId
    };
  }

  if (storedCompanyId) {
    return {
      ...pathParams,
      companyId: storedCompanyId
    };
  }

  // Return original pathParams if no ID could be found
  // The API calls will likely fail, but this avoids silent failures
  return pathParams;
};

// Controller for handling REST API calls
const restApiController = {
  // Method to handle GET requests
  async get(path: string, options: any = {}) {
    // Append query string parameters if provided
    let fullPath = path;
    if (
      options.queryStringParameters &&
      Object.keys(options.queryStringParameters).length > 0
    ) {
      const queryParts: string[] = [];

      // Process each query parameter
      Object.entries(options.queryStringParameters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Arrays are processed differently to ensure each element is represented
          // as a separate key=value pair in the query string. This is necessary
          // for DELETE operations where multiple values for the same key need
          // to be explicitly included in the query string.
          value.forEach((item) => {
            queryParts.push(`${key}=${encodeURIComponent(item)}`);
          });
        } else {
          // For non-arrays, just add a single key=value pair
          queryParts.push(`${key}=${encodeURIComponent(value as string)}`);
        }
      });

      const queryString = queryParts.join("&");
      fullPath = `${path}${path.includes("?") ? "&" : "?"}${queryString}`;
    }
    // Make the GET request
    const response = await authedFetch.get(fullPath);
    // Return the response data
    return response.data;
  },
  // Method to handle POST requests
  async post(path: string, options: any) {
    // Make the POST request with the provided body
    const response = await authedFetch.post(path, options.body);
    // Return the response data
    return response.data;
  },
  // Method to handle PATCH requests
  async patch(path: string, options: any) {
    // Make the PATCH request with the provided body
    const response = await authedFetch.patch(path, options.body);
    // Return the response data
    return response.data;
  },
  // Method to handle PUT requests
  async put(path: string, options: any) {
    let fullPath = path;
    if (
      options.queryStringParameters &&
      Object.keys(options.queryStringParameters).length > 0
    ) {
      const queryParts: string[] = [];

      // Process each query parameter
      Object.entries(options.queryStringParameters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Arrays are processed differently to ensure each element is represented
          // as a separate key=value pair in the query string. This is necessary
          // for PUT operations where multiple values for the same key need
          // to be explicitly included in the query string.
          value.forEach((item) => {
            queryParts.push(`${key}=${encodeURIComponent(item)}`);
          });
        } else {
          // For non-arrays, just add a single key=value pair
          queryParts.push(`${key}=${encodeURIComponent(value as string)}`);
        }
      });

      const queryString = queryParts.join("&");
      fullPath = `${path}${path.includes("?") ? "&" : "?"}${queryString}`;
    }

    // Make the PUT request
    const response = await authedFetch.put(fullPath, options.body);
    // Return the response data
    return response.data;
    // Return the response data
  },
  // Method to handle DELETE requests
  async del(path: string, options: any = {}) {
    // Append query string parameters if provided
    let fullPath = path;
    if (
      options.queryStringParameters &&
      Object.keys(options.queryStringParameters).length > 0
    ) {
      const queryParts: string[] = [];

      // Process each query parameter
      Object.entries(options.queryStringParameters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Arrays are processed differently to ensure each element is represented
          // as a separate key=value pair in the query string. This is necessary
          // for DELETE operations where multiple values for the same key need
          // to be explicitly included in the query string.
          value.forEach((item) => {
            queryParts.push(`${key}=${encodeURIComponent(item)}`);
          });
        } else {
          // For non-arrays, just add a single key=value pair
          queryParts.push(`${key}=${encodeURIComponent(value as string)}`);
        }
      });

      const queryString = queryParts.join("&");
      fullPath = `${path}${path.includes("?") ? "&" : "?"}${queryString}`;
    }

    // Make the DELETE request
    const response = await authedFetch.delete(fullPath);
    // Return the response data
    return response.data;
  }
};
// Mapping of HTTP methods to corresponding controller methods
const apiMethodsByType = {
  DEL: restApiController.del.bind(restApiController),
  GET: restApiController.get.bind(restApiController),
  POST: restApiController.post.bind(restApiController),
  PATCH: restApiController.patch.bind(restApiController),
  PUT: restApiController.put.bind(restApiController)
} as const;

export interface ApiError extends Error {
  response?: {
    data: any; // The data returned from the API
    status: number; // The HTTP status code
  };
}

export type Param = string | boolean | number | undefined | null;

export type PathParams = Record<string, Param>;

export type Params = Record<string, Param | Array<Param>>;

type QueryParams = Record<string, any>;

type ApiMethodKey = keyof typeof apiMethodsByType;

interface DataAction {
  httpMethod: ApiMethodKey;
  path: string;
}

type DataActionMap = Record<string, DataAction>;

interface DataTypeConfig {
  path: string;
  actions?: DataActionMap;
}

type DataTypeConfigMap = Partial<Record<DataType, DataTypeConfig>>;

interface FeatureConfig {
  entityTypeConfig?: DataTypeConfigMap;
  actions?: DataActionMap;
}

type FeatureConfigMap = Record<FeatureType, FeatureConfig>;

const baseCompanyPath = "/companies/:companyId";

const baseBulkPublishingPath = "/companies/:clientId/batch-publishing-jobs";

// Immutable data actions that are common across different data types
const immutableDataActions: DataActionMap = {
  create: {
    httpMethod: HttpMethod.Post, // HTTP method for creating a new resource
    path: ""
  },
  list: {
    httpMethod: HttpMethod.Get, // HTTP method for listing resources
    path: ""
  },
  retrieve: {
    httpMethod: HttpMethod.Get, // HTTP method for retrieving a specific resource
    path: "/:id"
  }
};

// Common data actions that include immutable actions plus additional actions
const commonDataActions: DataActionMap = {
  ...immutableDataActions,
  delete: {
    httpMethod: HttpMethod.Delete, // HTTP method for deleting a resource
    path: "/:id"
  },
  update: {
    httpMethod: HttpMethod.Patch, // HTTP method for updating a resource
    path: "/:id"
  }
};

const commonDataActionsForTransactions: DataActionMap = {
  ...immutableDataActions,
  delete: {
    httpMethod: HttpMethod.Delete, // HTTP method for deleting a resource
    path: "/:id"
  },
  bulkDelete: {
    httpMethod: HttpMethod.Delete, // HTTP method for deleting a resource
    path: ""
  },
  update: {
    httpMethod: HttpMethod.Put, // HTTP method for updating a resource
    path: "/:id/data"
  },
  search: {
    httpMethod: HttpMethod.Get,
    path: ""
  }
};

const commonDataActionsWithSearch: DataActionMap = {
  ...commonDataActions,
  search: {
    httpMethod: HttpMethod.Get,
    path: ""
  }
};

// Configuration map for different features and their associated data types
const featureConfigMap: FeatureConfigMap = {
  [FeatureType.Communications]: {
    entityTypeConfig: {
      [DataType.CommunicationsQuery]: {
        path: `${baseCompanyPath}/communication-queries`,
        actions: {
          create: {
            httpMethod: HttpMethod.Post,
            path: ""
          },
          notify: {
            httpMethod: HttpMethod.Post,
            path: "/notify"
          },
          list: {
            httpMethod: HttpMethod.Get,
            path: ""
          },
          listByDocument: {
            httpMethod: HttpMethod.Get,
            path: "/document/:document_id"
          },
          retrieve: {
            httpMethod: HttpMethod.Get,
            path: "/:query_id"
          },
          update: {
            httpMethod: HttpMethod.Patch,
            path: "/:query_id"
          },
          delete: {
            httpMethod: HttpMethod.Delete,
            path: "/:query_id"
          },
          close: {
            httpMethod: HttpMethod.Post,
            path: "/:query_id/close"
          },
          remind: {
            httpMethod: HttpMethod.Post,
            path: "/:query_id/remind"
          },
          createComment: {
            httpMethod: HttpMethod.Post,
            path: "/:query_id/comment"
          }
        }
      },
      [DataType.Chaser]: {
        path: `${baseCompanyPath}/chasers`,
        actions: {
          create: {
            httpMethod: HttpMethod.Post,
            path: ""
          }
        }
      },
      [DataType.DocumentQuery]: {
        path: `${baseCompanyPath}/documents/:document_id/queries`,
        actions: {
          list: {
            httpMethod: HttpMethod.Get,
            path: ""
          },
          update: {
            httpMethod: HttpMethod.Patch,
            path: "/:query_id"
          }
        }
      }
    }
  },
  [FeatureType.Bookkeeping]: {
    entityTypeConfig: {
      [DataType.Transaction]: {
        path: `${baseCompanyPath}/documents`,
        actions: { ...commonDataActionsForTransactions } // Common actions for transactions
      },
      [DataType.UploadDocuments]: {
        path: `${baseCompanyPath}/documents`,
        actions: { ...commonDataActions }
      },
      [DataType.DocumentsStatus]: {
        path: `${baseCompanyPath}/accounting-data-status`,
        actions: { ...commonDataActions }
      },
      [DataType.BulkPublishing]: {
        path: "",
        actions: {
          fetch: {
            httpMethod: HttpMethod.Get,
            path: baseBulkPublishingPath
          },
          validate: {
            httpMethod: HttpMethod.Post,
            path: `${baseBulkPublishingPath}/validate`
          },
          publish: {
            httpMethod: HttpMethod.Post,
            path: baseBulkPublishingPath
          },
          manualPublish: {
            httpMethod: HttpMethod.Put,
            path: `${baseCompanyPath}/documents/mark-published`
          }
        }
      }
    },
    actions: {
      updateDocumentStatus: {
        httpMethod: HttpMethod.Put,
        path: `${baseCompanyPath}/documents/:documentId/status`
      },
      fetchDuplicateData: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/documents/:documentId/duplicates`
      },
      deleteDuplicate: {
        httpMethod: HttpMethod.Patch,
        path: `${baseCompanyPath}/documents/:documentId`
      },
      markRequestAsNotDuplicate: {
        httpMethod: HttpMethod.Put,
        path: `${baseCompanyPath}/documents/:documentId/status`
      },
      loadTransactionHistory: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/documents/:documentId/history`
      },
      loadAccountingData: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/accounting-data-short`
      },
      loadTransactionComments: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/documents/:documentId/comments`
      },
      addTransactionComment: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/documents/:documentId/comments`
      },
      editTransactionComment: {
        httpMethod: HttpMethod.Patch,
        path: `${baseCompanyPath}/documents/:documentId/comments/:commentId`
      },
      deleteTransactionComment: {
        httpMethod: HttpMethod.Delete,
        path: `${baseCompanyPath}/documents/:documentId/comments/:commentId`
      },
      updateTransaction: {
        httpMethod: HttpMethod.Patch,
        path: `${baseCompanyPath}/documents/:id`
      },
      synchronizeTransaction: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/documents/:documentId/synchronize`
      },
      fetchAdjacentDocument: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/documents/:documentId/:direction` // :companyId is part of baseCompanyPath
      },
      loadDocumentImageURL: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/documents/:documentId/url`
      }
    }
  },
  [FeatureType.Reconciliation]: {
    entityTypeConfig: {
      [DataType.BankStatement]: {
        path: `${baseCompanyPath}/bank-accounts/:accountId/transactions`,
        actions: {
          ...commonDataActions,
          search: {
            httpMethod: HttpMethod.Get,
            path: ""
          }
        }
      },
      [DataType.Reconciliation]: {
        path: `${baseCompanyPath}/bank-accounts`,
        actions: { ...commonDataActions }
      },
      [DataType.BankStatementUploads]: {
        path: `${baseCompanyPath}/bank-accounts/:accountId/statements`,
        actions: { ...commonDataActions }
      }
    },
    actions: {
      loadReconciliations: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/bank-accounts`
      },
      loadBankStatements: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/bank-accounts/:accountId/transactions`
      },
      loadBankStatementSummary: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/bank-accounts/:accountId/reconciliation-summary`
      },
      loadStatementDetails: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/bank-accounts/:accountId/transactions/:statementId`
      },
      matchDocumentToStatement: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/bank-accounts/:accountId/transactions/:statementId/match`
      },
      unmatchDocumentFromStatement: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/bank-accounts/:accountId/transactions/:statementId/unmatch`
      },
      manualMatch: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/bank-accounts/:accountId/transactions/:statementId/match`
      },
      bulkManualMatch: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/bank-accounts/:accountId/transactions/bulk-manual-match`
      },
      addBank: {
        httpMethod: HttpMethod.Post,
        path: `/institutions`
      },
      createBankAccount: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/bank-accounts`
      },
      uploadBankStatement: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/bank-accounts/:accountId/statements`
      },
      convertStatementToTransactions: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/bank-accounts/:accountId/statements/:documentId/convert-to-transactions`
      },
      deleteBankAccount: {
        httpMethod: HttpMethod.Delete,
        path: `${baseCompanyPath}/bank-accounts/:accountId`
      },
      removeBankConnection: {
        httpMethod: HttpMethod.Delete,
        path: `${baseCompanyPath}/bank-connections/:connectionId`
      }
    }
  },
  [FeatureType.Settings]: {
    entityTypeConfig: {
      [DataType.User]: {
        path: "/users/current",
        actions: {
          ...commonDataActions,
          updateUserMethod: {
            httpMethod: HttpMethod.Patch,
            path: ""
          }
        }
      },
      [DataType.HubSpotToken]: {
        path: "/users/current/hubspot-token",
        actions: {
          generate: {
            httpMethod: HttpMethod.Post,
            path: ""
          }
        }
      },
      [DataType.LineItemExtraction]: {
        path: `/companies/:clientId/accounting-data/vendors`,
        actions: {
          ...commonDataActions,
          loadSuppliers: {
            httpMethod: HttpMethod.Get,
            path: "?offset=:offset&limit=:limit&search=:search"
          },
          updateSuppliers: {
            httpMethod: HttpMethod.Put,
            path: "/line-item-extraction"
          }
        }
      },
      [DataType.Notification]: {
        path: "/companies/:companyId/notifications",
        actions: {
          list: {
            httpMethod: HttpMethod.Get,
            path: ""
          },
          bulkDelete: {
            httpMethod: HttpMethod.Delete,
            path: ""
          },
          delete: {
            httpMethod: HttpMethod.Delete,
            path: "/:id"
          },
          markAsRead: {
            httpMethod: HttpMethod.Put,
            path: "/:id/read"
          },
          markAllAsRead: {
            httpMethod: HttpMethod.Put,
            path: "/read-all"
          }
        }
      }
    }
  },
  [FeatureType.Util]: {
    entityTypeConfig: {},
    actions: {}
  },
  [FeatureType.Organisation]: {
    entityTypeConfig: {
      [DataType.Summary]: {
        path: "/companies/:clientId/billing/summary",
        actions: {
          ...commonDataActions,
          getSummaryInRange: {
            httpMethod: HttpMethod.Get,
            path: "?after=:after&before=:before"
          }
        }
      },
      [DataType.Transactions]: {
        path: "/billing",
        actions: {
          ...commonDataActions,
          getCompanyTransactions: {
            httpMethod: HttpMethod.Get,
            path: "?company_id=:company_id&order_by=:order_by&limit=20&offset=:offset&after=:after&before=:before"
          }
        }
      },
      [DataType.EmailForwardingSender]: {
        path: `${baseCompanyPath}/email_forwarding_senders`,
        actions: {
          ...commonDataActions,
          upload: {
            httpMethod: HttpMethod.Post,
            path: "/upload"
          }
        }
      },
      [DataType.BankStatementDocument]: {
        path: `${baseCompanyPath}/bank-statements`,
        actions: {
          ...commonDataActions,
          getStatementsWithParams: {
            httpMethod: HttpMethod.Get,
            path: "?search=:search"
          },
          downloadPDF: {
            httpMethod: HttpMethod.Get,
            path: "/download-pdf"
          },
          downloadCSV: {
            httpMethod: HttpMethod.Get,
            path: "/download-csv"
          }
        }
      },
      [DataType.Team]: {
        path: `${baseCompanyPath}/members`,
        actions: {
          ...commonDataActions,
          getMembersWithParams: {
            httpMethod: HttpMethod.Get,
            path: "?search=:search&offset=:offset&limit=:limit"
          },
          resendEmailConfirmation: {
            httpMethod: HttpMethod.Post,
            path: "/:memberId/resend-email-confirmation"
          }
        }
      },
      [DataType.Clients]: {
        path: `${baseCompanyPath}/clients`,
        actions: {
          ...commonDataActionsWithSearch,
          getClientsWithParams: {
            httpMethod: HttpMethod.Get,
            path: "?search=:search&offset=:offset&limit=:limit"
          }
        }
      },
      [DataType.Company]: {
        path: `${baseCompanyPath}`,
        actions: { ...commonDataActions } // Common actions for companies
      },
      [DataType.CompanyBillingDetails]: {
        path: `${baseCompanyPath}/billing-details`,
        actions: {
          get: { httpMethod: HttpMethod.Get, path: "" } // Only need GET for now
        }
      },
      [DataType.Update]: {
        path: "/companies",
        actions: { ...commonDataActions } // Common actions for update
      },
      [DataType.Department]: {
        path: `${baseCompanyPath}/departments`,
        actions: { ...commonDataActions } // Common actions for departments
      },
      [DataType.DisconnectMember]: {
        path: `${baseCompanyPath}/members/:memberId/disconnect/:platform`,
        actions: { ...commonDataActions }
      },
      [DataType.InviteMember]: {
        path: `${baseCompanyPath}/members/:memberId/connections?platform=:platform`,
        actions: { ...commonDataActions }
      }
    }
  },
  [FeatureType.Integrations]: {
    entityTypeConfig: {
      [DataType.Integrations]: {
        path: `${baseCompanyPath}/accounting-integration-status`,
        actions: { ...commonDataActions }
      },
      [DataType.Accounting]: {
        path: `${baseCompanyPath}/accounting-data-status`,
        actions: { ...commonDataActions }
      },
      [DataType.Banks]: {
        path: "/institutions",
        actions: { ...commonDataActions }
      },
      [DataType.YapilyAuth]: {
        path: `${baseCompanyPath}/banks/authorize`,
        actions: { ...commonDataActions }
      },
      [DataType.BankConnections]: {
        path: `${baseCompanyPath}/bank-connections`,
        actions: { ...commonDataActions }
      },
      [DataType.AccountingShort]: {
        path: `${baseCompanyPath}/accounting-data-short`,
        actions: { ...commonDataActions }
      },
      [DataType.AccountingIntegration]: {
        path: `${baseCompanyPath}/accounting-integrations?platform=:platform`,
        actions: { ...commonDataActions }
      },
      [DataType.IntegrationAuth]: {
        path: `${baseCompanyPath}/integrations/:platform/authorize`,
        actions: { ...commonDataActions }
      },
      [DataType.MailAuth]: {
        path: `${baseCompanyPath}/mailing/:platform/authorize`,
        actions: { ...commonDataActions }
      },
      [DataType.IntegrationRefresh]: {
        path: `${baseCompanyPath}/integrations/:platform/refresh`,
        actions: { ...commonDataActions }
      },
      [DataType.IntegrationDisconnect]: {
        path: `${baseCompanyPath}/integrations/:platform/disconnect`,
        actions: { ...commonDataActions }
      },
      [DataType.WhatsappDisconnect]: {
        path: `${baseCompanyPath}/whatsapp/disconnect`,
        actions: { ...commonDataActions }
      },
      [DataType.MailDisconnect]: {
        path: `${baseCompanyPath}/mailing/:platform/disconnect`,
        actions: { ...commonDataActions }
      },
      [DataType.LinkBusiness]: {
        path: `${baseCompanyPath}/integrations/:platform/business`,
        actions: {
          ...commonDataActions,
          link: {
            httpMethod: HttpMethod.Put,
            path: ""
          }
        }
      },
      [DataType.ChartOfAccounts]: {
        path: `${baseCompanyPath}/accounting-data-paginated?search=:search&offset=:offset&limit=:limit`,
        actions: { ...commonDataActions }
      },
      [DataType.EmailStatus]: {
        path: `${baseCompanyPath}/mailing/:platform/status`,
        actions: { ...commonDataActions }
      },
      [DataType.WhatsappStatus]: {
        path: `/whatsapp/current`,
        actions: { ...commonDataActions }
      },
      [DataType.WhatsappNumber]: {
        path: `/whatsapp/connections`,
        actions: { ...commonDataActions }
      },
      [DataType.ConnectionDuration]: {
        path: `${baseCompanyPath}/bank-connections/:connectionId/synchronize`,
        actions: { ...commonDataActions }
      },
      [DataType.UpdateWhatsappNumber]: {
        path: `/whatsapp/connections`,
        actions: { ...commonDataActions }
      }
    },
    actions: {
      loadAccountingDataStatus: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/accounting-data-status`
      },
      loadCompanyAccountingIntegrations: {
        httpMethod: HttpMethod.Get,
        path: `${baseCompanyPath}/accounting-integrations`
      },
      extendPermissions: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/bank-connections/:connectionId/extend`
      },
      cancelYapilyConnection: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/bank-connections/:connectionId/revoke`
      },
      verifyWhatsappOTP: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/whatsapp/verify`
      },
      requestOTPResend: {
        httpMethod: HttpMethod.Post,
        path: `${baseCompanyPath}/whatsapp/otp/resend`
      },
      resendWhatsappInvite: {
        httpMethod: HttpMethod.Post,
        path: `/whatsapp/connections/:connectionId/resend-invite`
      },
      disconnectWhatsapp: {
        httpMethod: HttpMethod.Delete,
        path: `/whatsapp/connections/:connectionId`
      }
    }
  }
};

// Create a lookup map to find the parent feature type for a given data type
const featureLookupMap = Object.values(DataType).reduce(
  (result, dataType) => {
    // Find the feature cluster that contains the current data type
    const parentFeatureCluster = Object.entries(featureConfigMap).find(
      ([_featureCluster, featureConfig]) => {
        if (!featureConfig.entityTypeConfig) return false;

        // Check if the data type exists in the entity type configuration
        return Object.keys(featureConfig.entityTypeConfig).includes(dataType);
      }
    )?.[0];

    // If a parent feature cluster is found, add it to the result map
    if (parentFeatureCluster) {
      // eslint-disable-next-line no-param-reassign
      result[dataType] = parentFeatureCluster as FeatureType;
    }

    return result;
  },
  {} as Record<DataType, FeatureType>
);

// Function to look up the feature type from a given data type
const lookUpFeatureFromDataType = (
  dataType: DataType
): FeatureType | undefined => featureLookupMap[dataType];

// Function to clean query parameters by removing undefined or null values
// and converting arrays to comma-separated strings
const cleanParams = (params: Params): Params =>
  Object.entries(params).reduce<Params>((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        acc[key] = value.join(",");
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {});

// Special function for DEL and GET operations that doesn't join array values
// so they can be handled properly in the bound method
const cleanParamsForQuery = (params: Params): Params =>
  Object.entries(params).reduce<Params>((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value; // Keep arrays as arrays
    }
    return acc;
  }, {});

// Regular expression to match path placeholders (e.g., ":id")
const pathPlaceholderRegexp = /:(\w+)/g;

// Function to build a path by replacing placeholders with actual values
const buildPath = (
  placeholderPath: string,
  pathParams: Record<string, any>
): string =>
  placeholderPath.replace(pathPlaceholderRegexp, (_fullMatch, paramKey) => {
    if (paramKey in pathParams) {
      return String(pathParams[paramKey]);
    }
    throw new Error(`Path param "${paramKey}" not provided.`);
  });

// Function to perform a data action (e.g., GET, POST) based on the provided configuration
export const performDataAction = (
  dataType: DataType,
  actionName: string,
  pathParams: PathParams = {},
  queryParams: QueryParams = {},
  data: Record<string, any> = {}
) => {
  // Look up the feature type associated with the given data type
  const lookedUpFeature = lookUpFeatureFromDataType(dataType);

  // Get the configuration for the data type within the looked-up feature
  const actionConfig =
    featureConfigMap[lookedUpFeature!].entityTypeConfig?.[dataType];

  // Throw an error if the configuration is not found
  if (!actionConfig) {
    throw new Error(
      `Config not found for '${dataType}' in '${lookedUpFeature}'.`
    );
  }

  const { actions } = actionConfig;

  // Get the specific action configuration (e.g., create, update) for the data type
  const action = actions?.[actionName];

  // Throw an error if the action is not found
  if (!action)
    throw new Error(
      `Action ${actionName} not found for '${dataType}' in '${lookedUpFeature}'.`
    );

  // Ensure companyId is present in pathParams
  const enrichedPathParams = ensureCompanyId(pathParams);

  // Build the full path by combining the base path and action path, replacing placeholders
  const fullPath = buildPath(
    actionConfig.path + action.path,
    enrichedPathParams
  );

  // Use cleanParamsForDelete for DELETE operations and cleanParams for other operations
  const cleanedParams =
    action.httpMethod === HttpMethod.Delete ||
    action.httpMethod === HttpMethod.Get ||
    action.httpMethod === HttpMethod.Put
      ? cleanParamsForQuery(queryParams)
      : cleanParams(queryParams);

  const metricName = `API.DataAction.${dataType}.${actionName}`;
  const context = {
    dataType,
    actionName,
    path: fullPath,
    method: action.httpMethod,
    queryParams: cleanedParams
  };

  // If it's a status call, execute directly without monitoring
  if (fullPath.includes("/status")) {
    return apiMethodsByType[action.httpMethod as ApiMethodKey](fullPath, {
      queryStringParameters: cleanedParams,
      body: data
    });
  }

  // Otherwise, proceed with monitoring
  return performanceMonitor.measureAsync(
    metricName,
    () =>
      apiMethodsByType[action.httpMethod as ApiMethodKey](fullPath, {
        queryStringParameters: cleanedParams,
        body: data
      }),
    context
  );
};

export const performFeatureAction = (
  featureType: FeatureType,
  actionName: string,
  pathParams: PathParams = {},
  queryParams: QueryParams = {},
  data: Record<string, any> = {}
) => {
  const { actions } = featureConfigMap[featureType];

  const action = actions?.[actionName];
  if (!action)
    throw new Error(`Action ${actionName} not found in '${featureType}'.`);

  // Ensure companyId is present in pathParams
  const enrichedPathParams = ensureCompanyId(pathParams);

  const fullPath = buildPath(action.path, enrichedPathParams);

  // Use cleanParamsForDelete for DELETE operations and cleanParams for other operations
  const cleanedParams =
    action.httpMethod === HttpMethod.Delete ||
    action.httpMethod === HttpMethod.Get
      ? cleanParamsForQuery(queryParams)
      : cleanParams(queryParams);

  const metricName = `API.FeatureAction.${featureType}.${actionName}`;
  const context = {
    featureType,
    actionName,
    path: fullPath,
    method: action.httpMethod,
    queryParams: cleanedParams
  };

  // If it's a status call, execute directly without monitoring
  if (fullPath.includes("/status")) {
    return apiMethodsByType[action.httpMethod as ApiMethodKey](fullPath, {
      queryStringParameters: cleanedParams,
      body: data
    });
  }

  // Do an API call with data and params here and return promise
  return performanceMonitor.measureAsync(
    metricName,
    () =>
      apiMethodsByType[action.httpMethod as ApiMethodKey](fullPath, {
        queryStringParameters: cleanedParams,
        body: data
      }),
    context
  );
};

/**
 * Retrieves a list of data based on the specified data type.
 *
 * @param dataType - The type of data to retrieve.
 * @param params - Optional parameters for the data retrieval.
 * @param pathParams - Optional path parameters for the data retrieval.
 * @returns A Promise that resolves to the retrieved data.
 */
export const list = (
  dataType: DataType,
  params: Params = {},
  pathParams: PathParams = {}
) => performDataAction(dataType, "list", pathParams, params);

/**
 * Creates a new data entry.
 *
 * @param dataType - The type of data.
 * @param data - The data to be created.
 * @param pathParams - The path parameters for the request (optional).
 * @returns A promise that resolves to the created data.
 */
export const create = (
  dataType: DataType,
  data: any,
  pathParams: PathParams = {}
) => performDataAction(dataType, "create", pathParams, undefined, data);

/**
 * Retrieves data of the specified type.
 *
 * @param dataType - The type of data to retrieve.
 * @param pathParams - The path parameters for the data retrieval.
 * @returns A promise that resolves to the retrieved data.
 */
export const retrieve = (dataType: DataType, pathParams: PathParams = {}) =>
  performDataAction(dataType, "retrieve", pathParams);

/**
 * Deletes data of a specific type by ID.
 *
 * @param dataType - The type of data to delete.
 * @param id - The ID of the data to delete.
 * @param pathParams - Optional path parameters for the API endpoint.
 * @returns A promise that resolves when the data is successfully deleted.
 */
export const del = (
  dataType: DataType,
  id: string,
  pathParams: PathParams = {}
) => performDataAction(dataType, "delete", { ...pathParams, id });

/**
 * Updates the specified data type with the provided data.
 *
 * @param dataType - The type of data to update.
 * @param data - The data to update.
 * @param pathParams - The optional path parameters.
 * @returns A promise that resolves to the result of the update operation.
 */
export const update = (
  dataType: DataType,
  data: any,
  pathParams: PathParams = {}
) => performDataAction(dataType, "update", pathParams, undefined, data);
